import type { KgInsightSentiment } from '@api/api/kg-insights';
import { roundPercentages } from '@design/charts/pie-chart/utils';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { Markdown } from '@terminal/components/markdown/Markdown';
import { useMonthlySentimentState } from '@terminal/react-query-hooks/themes/useMonthlySentimentState';
import type {
  PulseMonthlySummaries,
  PulseSentimentBreakdown,
  PulseSentimentDirection,
  PulseSubject,
} from '@terminal/views/theme-overview/tab-content/pulse-overview/pulse-types';
import { stripDailySummaryCitations } from '@terminal/views/theme-overview/tab-content/pulse-overview/utils/linkDailySummaryCitations';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import styles from './MonthlyCondensedSentiment.module.scss';

export interface MonthlyCondensedSentimentProps {
  summaries?: PulseMonthlySummaries;
  totals?: PulseSentimentBreakdown['totals'];
  isSummariesLoading: boolean;
  isSentimentBreakdownLoading: boolean;
  subject: PulseSubject;
}

const sentimentCards: Exclude<PulseSentimentDirection, 'all'>[] = [
  'tailwind',
  'headwind',
];

const EMPTY_STATE_DIRECTION_LABELS = {
  theme: { tailwind: 'tailwind', headwind: 'headwind' },
  entity: { tailwind: 'bullish', headwind: 'bearish' },
};

const SENTIMENT_CARD_TITLE_KEYS = {
  theme: {
    tailwind: 'widget:themeOverview.pulse.tailwindSentiment',
    headwind: 'widget:themeOverview.pulse.headwindSentiment',
  },
  entity: {
    tailwind: 'widget:assetOverview.sentiment.directionSentiment_tailwind',
    headwind: 'widget:assetOverview.sentiment.directionSentiment_headwind',
  },
};

const sentimentLegend: Array<{
  key: KgInsightSentiment;
  labelKey: 'tailwind' | 'headwind' | 'neutral';
}> = [
  { key: 'positive', labelKey: 'tailwind' },
  { key: 'negative', labelKey: 'headwind' },
  { key: 'neutral', labelKey: 'neutral' },
];

export const MonthlyCondensedSentiment = ({
  summaries,
  totals,
  isSummariesLoading,
  isSentimentBreakdownLoading,
  subject,
}: MonthlyCondensedSentimentProps) => {
  const { t } = useTranslation(['widget']);
  const tailwindState = useMonthlySentimentState({
    summary: summaries?.tailwind,
    subject,
    direction: 'tailwind',
  });
  const headwindState = useMonthlySentimentState({
    summary: summaries?.headwind,
    subject,
    direction: 'headwind',
  });

  const states = { tailwind: tailwindState, headwind: headwindState };
  const percentages = roundPercentages(
    sentimentLegend.map(({ key }) => totals?.[key] ?? 0)
  );

  return (
    <section
      className={styles.root}
      data-testid="industry-drawer-monthly-sentiment"
    >
      <div className={styles.header}>
        <h3 className={styles.title}>
          {t('widget:themeOverview.pulse.drawer.title_all')}
        </h3>
        <div className={styles.legend}>
          {totals && !isSentimentBreakdownLoading ? (
            sentimentLegend.map(({ key, labelKey }, index) => {
              if (
                totals[key] === 0 ||
                (tailwindState !== 'summary' && headwindState !== 'summary')
              ) {
                return null;
              }

              const sentiment =
                subject.type === 'entity' && key !== 'neutral'
                  ? t(
                      key === 'positive'
                        ? 'widget:insights.insightDirection_bullish'
                        : 'widget:insights.insightDirection_bearish'
                    )
                  : t(`widget:themeOverview.pulse.${labelKey}`);
              const percent = `${percentages[index]}%`;

              return (
                <span key={key} className={styles.legendItem}>
                  <span
                    className={clsx(styles.legendDot, styles[key])}
                    aria-hidden
                  />
                  {t('widget:themeOverview.pulse.sentimentPercent', {
                    sentiment,
                    percent,
                  })}
                </span>
              );
            })
          ) : (
            <SkeletonLoader
              className={styles.legendSkeleton}
              areas={sentimentLegend.map(() => ({
                className: styles.legendSkeletonItem,
              }))}
            />
          )}
        </div>
      </div>
      {isSummariesLoading ? (
        <SkeletonLoader
          areas={[
            {
              styles: { width: '100%', height: '200px', borderRadius: '8px' },
            },
          ]}
        />
      ) : (
        <ErrorBoundaryWrapper showBorder={false}>
          <div className={styles.cards}>
            {sentimentCards.map(direction => {
              const narrative = summaries?.[direction]?.narrative;
              const state = states[direction];

              return (
                <article
                  key={direction}
                  className={clsx(styles.card, styles[direction])}
                  data-testid={`industry-drawer-sentiment-card-${direction}`}
                >
                  <span className={styles.cardTitle}>
                    {t(SENTIMENT_CARD_TITLE_KEYS[subject.type][direction])}
                  </span>
                  <span
                    className={clsx(styles.narrative, {
                      [styles.emptyNarrative]: state !== 'summary',
                    })}
                  >
                    {state === 'loading' ? (
                      <SkeletonLoader
                        areas={[
                          {
                            styles: {
                              width: '100%',
                              height: '40px',
                              borderRadius: '8px',
                            },
                          },
                        ]}
                      />
                    ) : state !== 'summary' ? (
                      t(
                        state === 'drivers'
                          ? 'widget:themeOverview.pulse.noDirectionKeyDriversInLast30Days'
                          : 'widget:themeOverview.pulse.noDirectionDriversInLast30Days',
                        {
                          direction:
                            EMPTY_STATE_DIRECTION_LABELS[subject.type][
                              direction
                            ],
                        }
                      )
                    ) : (
                      <Markdown
                        message={stripDailySummaryCitations(narrative ?? '')}
                      />
                    )}
                  </span>
                </article>
              );
            })}
          </div>
        </ErrorBoundaryWrapper>
      )}
    </section>
  );
};
